import React from 'react';
import { CloseButton } from '@UI-Elements/Buttons/CloseButton';
import { Button, CircularProgress, Input, MenuItem, Select } from '@mui/material';
import { Add, DeleteForeverOutlined } from '@mui/icons-material';
import { useAccountStore } from '../../../components/Stores/accountStore';
import { useMaterialsStore } from '../../../components/Stores/MaterialsStore';
import { addMonths } from 'date-fns';
import { Timestamp } from 'firebase/firestore';
import { calcExposureCalendar } from '../../BalanceFlow/balanceUtils';
import { savePosition } from '../../../sp-firebase/financialPositionsFB';
import { IFinancialPosition } from '../../../sp-types/financialPositionType';
import { triggerExposureCalc } from '../../../hooks/useExposure';

interface pRow {
  id: number;
  date: string;
  value: number;
  type: string;
}
const emptyRow: pRow = { id: 0, date: new Date().toISOString().split('T')[0], value: 100, type: 'long' };

export const FinancialPositionsPopup: React.FC<{ close: () => void }> = ({ close }) => {
  const { account, materialColorDictionary: colorsMap } = useAccountStore();
  const { materialsMap } = useMaterialsStore();

  const [rows, setRows] = React.useState<pRow[]>([emptyRow]);
  const [materialId, setMaterialId] = React.useState<string>('');
  const [loading, setLoading] = React.useState<boolean>(false);

  const updateRow = (id: number, patch: Partial<pRow>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };
  const removeRow = (id: number) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const getNextMonth = (date: string): string => addMonths(new Date(date), 1).toISOString().split('T')[0];

  const addRow = () => {
    setRows((prev) => [...prev, { ...emptyRow, id: prev.length + 1, date: getNextMonth(prev[prev.length - 1].date) }]);
  };

  const saveFinancialPoisitions = async () => {
    setLoading(true);

    const positions: IFinancialPosition[] = rows.map((r, i) => ({
      accountId: account.id || '',
      activityId: 'balance_' + i,
      createDate: Timestamp.now(),
      expiryDate: Timestamp.fromDate(new Date(r.date)),
      materialId,
      positionType: r.type === 'short' ? 'short' : 'long',
      routeId: '-1',
      id: `${account.id || ''}__${materialId}_${i}_${new Date().getTime()}`,
      routeQuantity: r.value,
      exposureCalendar: calcExposureCalendar(
        new Date(),
        new Date(r.date),
        r.value,
        r.type === 'short' ? 'short' : 'long',
      ),
      contracts: [],
    }));

    try {
      await Promise.all(positions.map((position, i) => savePosition(position, i)));

      const result = await triggerExposureCalc({ accountId: account.id || '', materialIds: [materialId] });

      console.log('Exposure calculation result:', result);
    } catch (error) {
      console.error('Error saving financial positions:', error);
    }

    setLoading(false);
    close();
  };

  return (
    <div className="flex flex-col p-4 gap-4 bg-white rounded shadow-custom relative">
      <div className="flex justify-between font-semibold">
        <div className="flex gap-3">
          Add Financial Positions to account: <p className="font-bold text-blue-300">{account.id}</p>
        </div>
        <CloseButton onClick={close} />
      </div>

      <div className="">
        Material:{' '}
        <Select onChange={({ target }) => setMaterialId(target.value)} className="h-8 w-60" value={materialId}>
          {account.materialsToAccount
            .filter(({ materialId }) => materialsMap[materialId])
            .map(({ materialId }, key) => (
              <MenuItem
                key={key}
                value={materialId}
                className="mb-1"
                style={{ borderLeft: `8px solid ${colorsMap[materialId]}` }}
              >
                {materialsMap[materialId] ? materialsMap[materialId].name : materialId}
              </MenuItem>
            ))}
        </Select>
      </div>
      <div className="flex flex-col gap-2">
        {rows.map((row) => (
          <div
            key={row.id}
            className="flex gap-2 border-y p-2 bg-gray-50"
            style={{ borderLeft: `8px solid ${colorsMap[materialId]}` }}
          >
            <Input
              type="date"
              className="border h-8 w-60"
              value={row.date}
              onChange={({ target }) => updateRow(row.id, { date: target.value })}
            />

            <input
              type="number"
              className="border h-8 w-60"
              value={row.value}
              onChange={({ target }) => updateRow(row.id, { value: +target.value })}
            />
            <span className="">mt</span>

            <Select
              value={row.type}
              className="border h-8 w-32"
              onChange={({ target }) => updateRow(row.id, { type: target.value })}
            >
              <MenuItem value="long">Long</MenuItem>
              <MenuItem value="short">Short</MenuItem>
            </Select>

            <Button onClick={() => removeRow(row.id)} title="Remove row">
              <DeleteForeverOutlined />
            </Button>
          </div>
        ))}
        <div>
          <Button variant="outlined" onClick={addRow}>
            <Add /> Add Row
          </Button>
        </div>
      </div>
      <div className="flex gap-4 justify-center">
        <Button variant="outlined" color="primary" className="w-64 px-4" onClick={close}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          className="w-64 px-4"
          onClick={saveFinancialPoisitions}
          disabled={!materialId}
        >
          Save
        </Button>
      </div>
      {loading && <CircularProgress size={60} className="absolute ` align-middle justify-center" />}
    </div>
  );
};
